import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, MoreHorizontal } from 'lucide-react';
type Message = {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  actions?: {
    label: string;
    sectionId: string;
  }[];
};
const AVATAR_URL = 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop&crop=face';
const QUICK_REPLIES = [{
  label: 'View Pricing',
  sectionId: 'pricing'
}, {
  label: 'Our Services',
  sectionId: 'solutions'
}, {
  label: 'Get Started',
  sectionId: 'contact'
}, {
  label: 'How It Works',
  sectionId: 'how-it-works'
}];
export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: Date.now(),
        text: 'Hey there! 👋 I am Sarah from Strattigize. How can I help you today? Feel free to ask about our services, pricing, or anything else!',
        sender: 'bot',
        timestamp: new Date(),
        actions: QUICK_REPLIES
      }]);
    }
  }, [isOpen, messages.length]);
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
      setIsOpen(false); // Optional: close chat when navigating
    }
  };
  const processMessage = (input: string) => {
    const lowerInput = input.toLowerCase();
    let responseText = '';
    let actions = QUICK_REPLIES;
    if (lowerInput.includes('price') || lowerInput.includes('pricing') || lowerInput.includes('cost') || lowerInput.includes('plan')) {
      responseText = 'We have three plans, all with no setup cost! Starter at £97/mo (Free Website, On-Page SEO, SSL & Fast Loading), Unlimited at £297/mo (adds CRM, Lead Tracking, Google 5 Star Reviews Automation, and 24/7 Live AI ChatBot), and Pro at £497/mo (adds 24/7 Live AI Receptionist and Automatic Appointments Booking Automation). Want me to take you to our pricing section?';
    } else if (lowerInput.includes('service') || lowerInput.includes('what do you do') || lowerInput.includes('offer')) {
      responseText = 'We automate your business! Websites, lead capture, CRM, missed call text-back, Google reviews automation, AI chatbots, and more. Everything runs 24/7 so you can focus on growth.';
    } else if (lowerInput.includes('contact') || lowerInput.includes('talk') || lowerInput.includes('speak') || lowerInput.includes('call')) {
      responseText = 'I would love to connect you with our team! Let me take you to the contact form where you can share your details and we will reach out within 24 hours.';
      actions = [{
        label: 'Go to Contact Form',
        sectionId: 'contact'
      }];
    } else if (lowerInput.includes('how') || lowerInput.includes('work') || lowerInput.includes('process')) {
      responseText = 'It is super simple! 1) Pick your plan, 2) We build everything for you, 3) You start saving time and making money. The whole setup takes 7-14 days.';
    } else if (lowerInput.includes('contract') || lowerInput.includes('cancel') || lowerInput.includes('commit')) {
      responseText = 'No long-term contracts at all! Everything is month-to-month. Cancel anytime with zero penalties.';
    } else if (lowerInput.includes('tech') || lowerInput.includes('savvy') || lowerInput.includes('hard') || lowerInput.includes('difficult')) {
      responseText = 'Not at all! We handle everything from setup to maintenance. Zero technical knowledge required on your end.';
    } else {
      responseText = 'Great question! I would recommend chatting with our team directly for the best answer. Want me to take you to the contact form?';
      actions = [{
        label: 'Contact Us',
        sectionId: 'contact'
      }, ...QUICK_REPLIES.slice(0, 2)];
    }
    return {
      text: responseText,
      actions
    };
  };
  const handleSend = (text: string = inputValue) => {
    if (!text.trim()) return;
    // Add user message
    const newUserMsg: Message = {
      id: Date.now(),
      text: text.trim(),
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMsg]);
    setInputValue('');
    setIsTyping(true);
    // Simulate typing delay (800-1200ms)
    const delay = Math.floor(Math.random() * 400) + 800;
    setTimeout(() => {
      const response = processMessage(text);
      const newBotMsg: Message = {
        id: Date.now() + 1,
        text: response.text,
        sender: 'bot',
        timestamp: new Date(),
        actions: response.actions
      };
      setIsTyping(false);
      setMessages(prev => [...prev, newBotMsg]);
    }, delay);
  };
  return <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end" data-id="element-3">
      <AnimatePresence data-id="element-4">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl shadow-2xl w-[350px] max-w-[calc(100vw-32px)] h-[500px] max-h-[calc(100vh-100px)] mb-4 flex flex-col overflow-hidden border border-gray-100" data-id="element-5">
            {/* Header */}
            <div className="gradient-animated p-4 flex items-center justify-between text-white" data-id="element-6">
              <div className="flex items-center space-x-3" data-id="element-7">
                <div className="relative" data-id="element-8">
                  <img src={AVATAR_URL} alt="Sarah" className="w-10 h-10 rounded-full object-cover border-2 border-white/20" data-id="element-9" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-primary rounded-full" data-id="element-10"></div>
                </div>
                <div data-id="element-11">
                  <h3 className="font-bold text-sm" data-id="element-12">Sarah</h3>
                  <p className="text-xs text-white/80" data-id="element-13">Strattigize Support</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors p-1" data-id="element-14">
                <X size={20} data-id="element-15" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50" data-id="element-16">
              {messages.map(msg => <motion.div key={msg.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-17">
                  <div className={`flex items-end space-x-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse space-x-reverse' : 'flex-row'}`} data-id="element-18">
                    {msg.sender === 'bot' && <img src={AVATAR_URL} alt="Sarah" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mb-1" data-id="element-19" />}
                    <div className={`p-3 rounded-2xl text-sm ${msg.sender === 'user' ? 'bg-primary text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 shadow-sm rounded-bl-sm'}`} data-id="element-20">
                      {msg.text}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  {msg.actions && msg.actions.length > 0 && <div className="mt-3 flex flex-wrap gap-2 pl-8" data-id="element-21">
                      {msg.actions.map((action, idx) => <button key={idx} onClick={() => scrollToSection(action.sectionId)} className="text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 border border-primary/20 px-3 py-1.5 rounded-full transition-colors" data-id="element-22">
                          {action.label}
                        </button>)}
                    </div>}
                </motion.div>)}

              {/* Typing Indicator */}
              {isTyping && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-end space-x-2" data-id="element-23">
                  <img src={AVATAR_URL} alt="Sarah" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mb-1" data-id="element-24" />
                  <div className="bg-white border border-gray-100 shadow-sm p-3 rounded-2xl rounded-bl-sm flex space-x-1" data-id="element-25">
                    <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: 0
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-26" />
                    <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: 0.2
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-27" />
                    <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                duration: 0.6,
                repeat: Infinity,
                delay: 0.4
              }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" data-id="element-28" />
                  </div>
                </motion.div>}
              <div ref={messagesEndRef} data-id="element-29" />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-gray-100" data-id="element-30">
              <form onSubmit={e => {
            e.preventDefault();
            handleSend();
          }} className="flex items-center space-x-2" data-id="element-31">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Type your message..." className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all" data-id="element-32" />
                <button type="submit" disabled={!inputValue.trim() || isTyping} className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-33">
                  <Send size={16} className="ml-0.5" data-id="element-34" />
                </button>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button whileHover={{
      scale: 1.08,
      y: -2
    }} whileTap={{
      scale: 0.92,
      y: 1
    }} onClick={() => setIsOpen(!isOpen)} className="w-16 h-16 rounded-full gradient-animated text-white flex items-center justify-center relative group ring-2 ring-white/20 ring-offset-2 ring-offset-transparent" style={{
      boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 10px 24px rgba(232,76,26,0.3), 0 20px 48px rgba(232,76,26,0.15), inset 0 1px 1px rgba(255,255,255,0.25)'
    }} data-id="element-35">
        <AnimatePresence mode="wait" data-id="element-36">
          {isOpen ? <motion.div key="close" initial={{
          opacity: 0,
          rotate: -90
        }} animate={{
          opacity: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          rotate: 90
        }} transition={{
          duration: 0.2
        }} data-id="element-37">
              <X size={28} data-id="element-38" />
            </motion.div> : <motion.div key="open" initial={{
          opacity: 0,
          rotate: 90
        }} animate={{
          opacity: 1,
          rotate: 0
        }} exit={{
          opacity: 0,
          rotate: -90
        }} transition={{
          duration: 0.2
        }} className="relative w-full h-full flex items-center justify-center" data-id="element-39">
              <MessageCircle size={28} className="group-hover:opacity-0 transition-opacity duration-300" data-id="element-40" />
              <img src={AVATAR_URL} alt="Chat with Sarah" className="absolute inset-0 w-full h-full rounded-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300 border-2 border-white/30 shadow-inner" data-id="element-41" />
            </motion.div>}
        </AnimatePresence>

        {/* Notification Badge */}
        {!isOpen && messages.length === 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full flex items-center justify-center" data-id="element-42">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-43"></span>
          </span>}
      </motion.button>
    </div>;
}